import React, {
  useState,
  forwardRef,
  useRef,
  useEffect,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text, InputPrimitive, isWeb } from "@xsolla/xui-primitives";
import {
  useResolvedTheme,
  useId,
  type ThemeOverrideProps,
} from "@xsolla/xui-core";
import {
  Visa,
  Mastercard,
  Maestro,
  AmericanExpress,
  Dinersclub,
  Discover,
  Jcb,
  Unionpay,
  Aura,
  CartesBancaires,
  Cirrus,
  Dankort,
  Elo,
  Hipercard,
  Mir,
  Naranja,
  Paypal,
  Sodexo,
  Uatp,
  type PaymentIconProps,
} from "@xsolla/xui-icons-payment";

/**
 * Supported payment system keys
 */
export type PaymentSystemKey =
  | "visa"
  | "mastercard"
  | "amex"
  | "diners"
  | "maestro"
  | "unionpay"
  | "discover"
  | "jcb"
  | "aura"
  | "cartesbancaires"
  | "cirrus"
  | "dankort"
  | "elo"
  | "hipercard"
  | "mir"
  | "naranja"
  | "paypal"
  | "sodexo"
  | "uatp";

/**
 * Default payment systems to display
 */
const DEFAULT_POSSIBLE_PAYMENTS: PaymentSystemKey[] = [
  "mastercard",
  "visa",
  "maestro",
  "diners",
  "amex",
  "discover",
  "jcb",
  "unionpay",
];

export interface InputPaymentProps
  extends
    Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "onChange">,
    ThemeOverrideProps {
  /**
   * Property for specifying the value of the control.
   */
  value?: string;
  /**
   * Property to display the icon to the left of the content.
   */
  icon?: ReactNode;
  /**
   * Property for specifying the placeholder of the control.
   */
  placeholder?: string;
  /**
   * Event handler when the value changes (for controlled mode).
   */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Event handler when the text changes (alternative to onChange).
   */
  onChangeText?: (text: string) => void;
  /**
   * Property for changing the size of the input.
   */
  size?: "xl" | "lg" | "md" | "sm" | "xs";
  /**
   * Property for specifying the name of the control.
   */
  name?: string;
  /**
   * Property for disabling the control and highlighting it as an inactive state.
   */
  disabled?: boolean;
  /**
   * Property for displaying an error message and highlighting the control as invalid.
   */
  errorMessage?: string;
  /**
   * Property for displaying an error and highlighting the control as invalid.
   */
  error?: boolean;
  /**
   * Property to specify the possible payment systems displayed on the right.
   */
  possiblePayments?: PaymentSystemKey[];
  /**
   * Property to set the maximum number of visible possible payment systems at the same time on the right side of the input.
   */
  maxVisiblePossiblePayments?: number;
  /**
   * Property to show the recognized payment system (all possible payment systems are hidden and only the current one passed by this prop is displayed).
   */
  recognizedPayment?: PaymentSystemKey;
  /**
   * Callback when card type is auto-detected from the input value.
   */
  onRecognizedPaymentChange?: (payment: PaymentSystemKey | null) => void;
  /**
   * Property to enable automatic payment system detection from input value.
   */
  autoDetect?: boolean;
  /**
   * Unique identifier for the input element. Used for accessibility linking.
   */
  id?: string;
  /**
   * Accessible label for screen readers when no visible label is present.
   */
  "aria-label"?: string;
  /**
   * Test identifier for the component.
   */
  testID?: string;
}

// Map payment system keys to icon components
const paymentIconComponents: Record<
  PaymentSystemKey,
  React.FC<PaymentIconProps>
> = {
  visa: Visa,
  mastercard: Mastercard,
  maestro: Maestro,
  amex: AmericanExpress,
  diners: Dinersclub,
  discover: Discover,
  jcb: Jcb,
  unionpay: Unionpay,
  aura: Aura,
  cartesbancaires: CartesBancaires,
  cirrus: Cirrus,
  dankort: Dankort,
  elo: Elo,
  hipercard: Hipercard,
  mir: Mir,
  naranja: Naranja,
  paypal: Paypal,
  sodexo: Sodexo,
  uatp: Uatp,
};

/**
 * Detects payment system from card number using BIN ranges.
 * Order matters - more specific patterns should come before general ones.
 */
const detectPaymentSystem = (cardNumber: string): PaymentSystemKey | null => {
  const cleanNumber = cardNumber.replace(/\s/g, "");

  if (!cleanNumber) return null;

  // UATP: starts with 1
  if (/^1/.test(cleanNumber)) return "uatp";

  // Mir: 2200-2204
  if (/^220[0-4]/.test(cleanNumber)) return "mir";

  // Amex: 34, 37
  if (/^3[47]/.test(cleanNumber)) return "amex";

  // Diners Club: 300-305, 36, 38
  if (/^3(?:0[0-5]|[68])/.test(cleanNumber)) return "diners";

  // JCB: 3528-3589
  if (/^35(?:2[89]|[3-8])/.test(cleanNumber)) return "jcb";

  // Elo: specific BIN ranges (must check before Visa/Mastercard due to overlapping ranges)
  if (
    /^(401178|401179|431274|438935|451416|457393|457631|457632|504175|506699|5067[0-9]{2}|509[0-9]{3}|627780|636297|636368|650[0-9]{3}|651[0-9]{3}|655[0-9]{3})/.test(
      cleanNumber
    )
  )
    return "elo";

  // Visa: starts with 4
  if (/^4/.test(cleanNumber)) return "visa";

  // Hipercard: 606282, 637095, 637568, 637599, 637609, 637612
  if (/^(606282|637095|637568|637599|637609|637612)/.test(cleanNumber))
    return "hipercard";

  // Aura: 507860
  if (/^507860/.test(cleanNumber)) return "aura";

  // Dankort: 5019
  if (/^5019/.test(cleanNumber)) return "dankort";

  // Naranja: 589562
  if (/^589562/.test(cleanNumber)) return "naranja";

  // Mastercard: 51-55 or 2221-2720
  // For 51-55 range
  if (/^5[1-5]/.test(cleanNumber)) return "mastercard";
  // For 2221-2720 range (need 4+ digits for accurate detection)
  if (
    cleanNumber.length >= 4 &&
    /^(?:222[1-9]|22[3-9]\d|2[3-6]\d{2}|27[01]\d|2720)/.test(cleanNumber)
  )
    return "mastercard";

  // Discover: 6011, 622126-622925, 644-649, 65
  if (/^(?:6011|65|64[4-9])/.test(cleanNumber)) return "discover";
  // For 622126-622925 range (need 6+ digits for accurate detection)
  if (
    cleanNumber.length >= 6 &&
    /^(?:6221(?:2[6-9]|[3-9]\d)|622[2-8]\d{2}|6229(?:[01]\d|2[0-5]))/.test(
      cleanNumber
    )
  )
    return "discover";

  // UnionPay: 62 (but not 622126-622925 which is Discover)
  if (/^62/.test(cleanNumber)) return "unionpay";

  // Maestro: 50, 56-69 (catch-all for remaining 5x and 6x ranges)
  if (/^(?:5[06-9]|6)/.test(cleanNumber)) return "maestro";

  return null;
};

// Card icon dimensions (4:3 aspect ratio)
const CARD_GAP = 4;

/**
 * Payment card icons component with sliding animation
 */
const PaymentIcons: React.FC<{
  possiblePayments: PaymentSystemKey[];
  maxVisible: number;
  recognizedPayment?: PaymentSystemKey | null;
  iconHeight: number;
}> = ({ possiblePayments, maxVisible, recognizedPayment, iconHeight }) => {
  const [cyclingIndex, setCyclingIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isRecognized, setIsRecognized] = useState(false);

  // Scale factor based on size (icons use 4:3 aspect ratio)
  const scaledCardHeight = iconHeight;
  const scaledCardWidth = Math.round(iconHeight * (4 / 3));
  const scaledGap = CARD_GAP;

  // Calculate which cards to show in cycling position
  const constantCards = possiblePayments.slice(0, maxVisible - 1);
  const cyclingCards = possiblePayments.slice(maxVisible - 1);

  // Handle recognized payment animation
  useEffect(() => {
    if (recognizedPayment) {
      setIsRecognized(true);
    } else {
      // Small delay before showing all cards again
      const timeout = setTimeout(() => {
        setIsRecognized(false);
      }, 50);
      return () => clearTimeout(timeout);
    }
  }, [recognizedPayment]);

  // Cycle through remaining cards
  useEffect(() => {
    if (cyclingCards.length <= 1 || recognizedPayment) return;

    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCyclingIndex((prev) => (prev + 1) % cyclingCards.length);
        setIsTransitioning(false);
      }, 150);
    }, 2000);

    return () => clearInterval(interval);
  }, [cyclingCards.length, recognizedPayment]);

  // Human-readable labels for payment systems
  const paymentLabels: Record<PaymentSystemKey, string> = {
    mastercard: "Mastercard",
    visa: "Visa",
    maestro: "Maestro",
    diners: "Diners Club",
    amex: "American Express",
    discover: "Discover",
    jcb: "JCB",
    unionpay: "UnionPay",
    aura: "Aura",
    cartesbancaires: "Cartes Bancaires",
    cirrus: "Cirrus",
    dankort: "Dankort",
    elo: "Elo",
    hipercard: "Hipercard",
    mir: "Mir",
    naranja: "Naranja",
    paypal: "PayPal",
    sodexo: "Sodexo",
    uatp: "UATP",
  };

  // Build aria label from possible payments
  const getAriaLabel = () => {
    if (recognizedPayment) {
      return `Recognized payment: ${paymentLabels[recognizedPayment]}`;
    }
    return `Accepted payment cards: ${possiblePayments
      .map((key) => paymentLabels[key])
      .join(", ")}`;
  };

  // Calculate total width for the container
  const totalVisibleCards = Math.min(possiblePayments.length, maxVisible);
  const containerWidth =
    totalVisibleCards * scaledCardWidth + (totalVisibleCards - 1) * scaledGap;

  return (
    <Box
      flexDirection="row"
      alignItems="center"
      role="img"
      aria-label={getAriaLabel()}
      position="relative"
      width={containerWidth}
      height={scaledCardHeight}
      style={{ overflow: "hidden" }}
    >
      {/* All constant cards */}
      {constantCards.map((key, index) => {
        const IconComponent = paymentIconComponents[key];
        const isRecognizedCard = recognizedPayment === key;

        // Calculate slide offset when recognized
        // Cards should slide to the right, except the recognized one
        let translateX = 0;
        let opacity = 1;

        if (isRecognized && recognizedPayment) {
          if (isRecognizedCard) {
            // Move recognized card to the rightmost position
            const targetPosition =
              (totalVisibleCards - 1) * (scaledCardWidth + scaledGap);
            const currentPosition = index * (scaledCardWidth + scaledGap);
            translateX = targetPosition - currentPosition;
            opacity = 1;
          } else {
            // Slide non-recognized cards to the right and fade out
            translateX = containerWidth;
            opacity = 0;
          }
        }

        return (
          <Box
            key={key}
            position="absolute"
            left={index * (scaledCardWidth + scaledGap)}
            style={{
              transform: isWeb ? `translateX(${translateX}px)` : undefined,
              opacity,
              ...(isWeb && {
                transition: "transform 300ms ease-out, opacity 300ms ease-out",
              }),
            }}
          >
            <IconComponent size={scaledCardHeight} />
          </Box>
        );
      })}

      {/* Cycling position */}
      {cyclingCards.length > 0 && (
        <Box
          position="absolute"
          left={(maxVisible - 1) * (scaledCardWidth + scaledGap)}
          width={scaledCardWidth}
          height={scaledCardHeight}
        >
          {cyclingCards.map((key, index) => {
            const IconComponent = paymentIconComponents[key];
            const isCurrentCycling = index === cyclingIndex;
            const isRecognizedCard = recognizedPayment === key;

            let translateX = 0;
            let opacity = isCurrentCycling && !isTransitioning ? 1 : 0;

            if (isRecognized && recognizedPayment) {
              if (isRecognizedCard) {
                // Keep in place, fully visible
                opacity = 1;
              } else {
                // Slide out to the right
                translateX = containerWidth;
                opacity = 0;
              }
            }

            return (
              <Box
                key={key}
                position="absolute"
                top={0}
                left={0}
                style={{
                  transform: isWeb ? `translateX(${translateX}px)` : undefined,
                  opacity,
                  ...(isWeb && {
                    transition: isRecognized
                      ? "transform 300ms ease-out, opacity 300ms ease-out"
                      : "opacity 150ms ease-in-out",
                  }),
                }}
              >
                <IconComponent size={scaledCardHeight} />
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export const InputPayment = forwardRef<HTMLInputElement, InputPaymentProps>(
  (
    {
      value,
      icon,
      placeholder = "Card number",
      onChange,
      onChangeText,
      onKeyDown,
      size = "md",
      name,
      disabled = false,
      errorMessage,
      error,
      possiblePayments = DEFAULT_POSSIBLE_PAYMENTS,
      maxVisiblePossiblePayments = 5,
      recognizedPayment: controlledRecognizedPayment,
      onRecognizedPaymentChange,
      autoDetect = true,
      id: providedId,
      "aria-label": ariaLabel,
      testID,
      themeMode,
      themeProductContext,
      ...rest
    },
    ref
  ) => {
    const { theme } = useResolvedTheme({ themeMode, themeProductContext });
    const [internalState, setInternalState] = useState<"default" | "focus">(
      "default"
    );
    const [passValue, setPassValue] = useState(value ?? "");
    const [detectedPayment, setDetectedPayment] =
      useState<PaymentSystemKey | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Generate unique IDs for accessibility
    const rawId = useId();
    const safeId = rawId.replace(/:/g, "");
    const inputId = providedId || `input-payment-${safeId}`;
    const errorId = `${inputId}-error`;

    // Forward ref to input element
    React.useImperativeHandle(
      ref,
      () => inputRef.current as HTMLInputElement,
      []
    );

    // Sync passValue with value prop when it changes
    useEffect(() => {
      if (value !== undefined) {
        setPassValue(value);
      }
    }, [value]);

    // Auto-detect payment system when value changes
    // Only detect cards that are in the possiblePayments list
    useEffect(() => {
      if (autoDetect && passValue) {
        const detected = detectPaymentSystem(passValue);
        // Only use the detected payment if it's in possiblePayments
        const validDetected =
          detected && possiblePayments.includes(detected) ? detected : null;
        setDetectedPayment(validDetected);
        onRecognizedPaymentChange?.(validDetected);
      } else if (!passValue) {
        setDetectedPayment(null);
        if (autoDetect) {
          onRecognizedPaymentChange?.(null);
        }
      }
    }, [passValue, autoDetect, onRecognizedPaymentChange, possiblePayments]);

    const isDisable = disabled;
    const isError = !!(errorMessage || error);
    const isFocus = internalState === "focus";

    // Determine which payment to display
    const displayRecognizedPayment =
      controlledRecognizedPayment ?? detectedPayment;

    // Resolve Config from Theme
    const sizeStyles = theme.sizing.input(size);
    const inputColors = theme.colors.control.input;

    const handleFocus = () => {
      if (!isDisable) {
        setInternalState("focus");
      }
    };

    const handleBlur = () => {
      if (!isDisable) {
        setInternalState("default");
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;

      if (onChange) {
        onChange(e);
      }
      if (onChangeText) {
        onChangeText(newValue);
      }

      setPassValue(newValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape") {
        e.currentTarget.blur();
      }
      if (onKeyDown) {
        onKeyDown(e);
      }
    };

    // Resolve background and border colors based on state
    let backgroundColor = inputColors.bg;
    let borderColor = inputColors.border;
    let outlineColor: string | undefined;

    if (isDisable) {
      backgroundColor = inputColors.bgDisable;
      borderColor = inputColors.borderDisable;
    } else if (isError) {
      outlineColor = theme.colors.border.alert;
      if (isFocus) {
        backgroundColor = theme.colors.control.focus.bg;
      }
    } else if (isFocus) {
      backgroundColor = theme.colors.control.focus.bg;
      outlineColor = theme.colors.border.brand;
    }

    const textColor = isDisable ? inputColors.textDisable : inputColors.text;
    const placeholderColor = inputColors.placeholder;
    const iconColor = inputColors.placeholder;

    // Padding values from Figma design
    const paddingConfig: Record<
      string,
      { vertical: number; horizontal: number }
    > = {
      xl: { vertical: 12, horizontal: 12 },
      lg: { vertical: 14, horizontal: 12 },
      md: { vertical: 11, horizontal: 12 },
      sm: { vertical: 7, horizontal: 10 },
      xs: { vertical: 7, horizontal: 10 },
    };

    // Icon sizes from Figma design
    const iconSizeConfig: Record<string, number> = {
      xl: 18,
      lg: 18,
      md: 18,
      sm: 16,
      xs: 16,
    };

    // Focus outline config from Figma design
    const focusOutlineConfig: Record<
      string,
      { width: number; offset: number }
    > = {
      xl: { width: 1, offset: -1 },
      lg: { width: 1, offset: -1 },
      md: { width: 1, offset: -1 },
      sm: { width: 1, offset: -1 },
      xs: { width: 1, offset: -1 },
    };

    const padding = paddingConfig[size];
    const borderRadius = theme.shape.input[size].borderRadius;
    const iconSize = iconSizeConfig[size];
    const focusOutline = focusOutlineConfig[size];

    return (
      <Box
        flexDirection="column"
        gap={sizeStyles.fieldGap}
        width="100%"
        testID={testID}
      >
        <Box
          backgroundColor={backgroundColor}
          borderColor={borderColor}
          borderWidth={borderColor !== "transparent" ? 1 : 0}
          borderRadius={borderRadius}
          height={sizeStyles.height}
          paddingVertical={padding.vertical}
          paddingHorizontal={padding.horizontal}
          flexDirection="row"
          alignItems="center"
          gap={10}
          position="relative"
          style={{
            ...(outlineColor
              ? {
                  outline: `${focusOutline.width}px solid ${outlineColor}`,
                  outlineOffset: `${focusOutline.offset}px`,
                }
              : {}),
          }}
          hoverStyle={
            !isDisable && !isFocus && !isError
              ? {
                  backgroundColor: inputColors.bgHover,
                  borderColor: inputColors.borderHover,
                }
              : undefined
          }
        >
          {icon && (
            <Box
              alignItems="center"
              justifyContent="center"
              role="img"
              aria-hidden={true}
            >
              {React.isValidElement(icon)
                ? React.cloneElement(
                    icon as React.ReactElement<{
                      size?: number;
                      color?: string;
                    }>,
                    {
                      size: iconSize,
                      color: iconColor,
                    }
                  )
                : icon}
            </Box>
          )}

          <Box flex={1} height="100%" justifyContent="center">
            <InputPrimitive
              ref={inputRef}
              id={inputId}
              value={passValue}
              name={name}
              placeholder={placeholder}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              disabled={isDisable}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              color={textColor}
              fontSize={sizeStyles.fontSize}
              placeholderTextColor={placeholderColor}
              aria-invalid={isError || undefined}
              aria-describedby={errorMessage ? errorId : undefined}
              aria-label={ariaLabel || "Card number"}
              aria-disabled={isDisable || undefined}
              data-testid="input-payment__field"
              {...rest}
            />
          </Box>

          {possiblePayments.length > 0 && (
            <Box
              alignItems="center"
              justifyContent="center"
              data-testid="input-payment__card-icons"
              style={{ overflow: "hidden" }}
            >
              <PaymentIcons
                possiblePayments={possiblePayments}
                maxVisible={maxVisiblePossiblePayments}
                recognizedPayment={displayRecognizedPayment}
                iconHeight={iconSize}
              />
            </Box>
          )}
        </Box>

        {errorMessage && (
          <Text
            id={errorId}
            role="alert"
            color={theme.colors.content.alert.primary}
            fontSize={sizeStyles.fontSize - 2}
          >
            {errorMessage}
          </Text>
        )}
      </Box>
    );
  }
);

InputPayment.displayName = "InputPayment";
